import React, { useEffect, useState, useContext, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ProductContext } from "../../context/AppContext";
import { SocketContext } from "../../context/socketContext";
import Header from "../Header";
import { all_routes } from "../router/all_routes";
import { Container, Card, Button, Form } from "react-bootstrap";
import Select from "react-select";
import Swal from "sweetalert2";

const normalizeModuleKey = (notification) => {
  const moduleKey =
    notification?.module_id ??
    notification?.moduleId ??
    notification?.module ??
    notification?.module_name;

  if (moduleKey === undefined || moduleKey === null) {
    return null;
  }

  const numericKey = Number(moduleKey);
  return Number.isNaN(numericKey) ? String(moduleKey) : numericKey;
};

const normalizeNotificationId = (notification) => {
  if (notification?.id !== undefined && notification?.id !== null) {
    return `id:${notification.id}`;
  }

  return [
    `module:${normalizeModuleKey(notification)}`,
    `body:${notification?.body || ""}`,
    `created:${notification?.created_at || ""}`,
    `user:${notification?.user_id || ""}`,
    `type:${notification?.type || ""}`,
  ].join("|");
};

const mergeNotifications = (primary = [], secondary = []) => {
  const deduped = new Map();

  [...primary, ...secondary]
    .filter(Boolean)
    .forEach((notification) => {
      const key = normalizeNotificationId(notification);
      if (!deduped.has(key)) {
        deduped.set(key, notification);
      }
    });

  const getSortTime = (notification) => {
    const timestamp =
      notification?.created_at ||
      notification?.received_at ||
      notification?.updated_at ||
      notification?.createdAt;

    const time = timestamp ? new Date(timestamp).getTime() : 0;
    return Number.isFinite(time) ? time : 0;
  };

  return [...deduped.values()].sort((a, b) => getSortTime(b) - getSortTime(a));
};

const ModuleWiseNotification = () => {
  const { userData, StaffData, getMenuList = [] } = useContext(ProductContext);
  const { socket, notificationlist = [], setNotificationList } = useContext(SocketContext) || {};
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const routes = all_routes;
  const authtoken = localStorage.getItem("token");
  const [displayNotifications, setDisplayNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [page, setPage] = useState(1);
  const pageSize = 10; // Show 10 notifications at a time

  // Filter state
  const [filters, setFilters] = useState({
    createdBy: null,
    date: "",
  });

  // Map module ID to name for display
  const moduleNames = {
    1: "MAIN",
    2: "Enquiry",
    3: "Visitor",
    4: "Masters",
    5: "Staff",
    6: "Student Information",
    7: "Time Table",
    8: "Communication",
    9: "Transport",
    10: "Academic",
    11: "MANAGEMENT",
    12: "HRM",
    13: "SMS",
    14: "Manage Role Menu",
    15: "Library",
  };

  // Format staff data for dropdown
  const staffOptions = Array.isArray(StaffData?.data?.data)
    ? StaffData.data.data.map((staff) => ({
        value: staff.id,
        label: `${staff.first_name} ${staff.last_name}`,
      }))
    : [];

  // Function to get sender name
  const getSenderName = (notification) => {
    if (notification.sender === "system") {
      return notification.username || "System";
    }
    const senderId = notification.user_id ?? notification.sender;
    const staff = staffOptions.find((s) => s.value === Number(senderId));
    return staff ? staff.label : notification.username || "N/A";
  };

  const getNotificationText = (notification) =>
    notification?.body ||
    notification?.message ||
    notification?.notification ||
    "No description available";

  const getNotificationStatus = (notification) =>
    notification?.response_status ||
    notification?.responseStatus ||
    "Unknown";

  const currentModuleLabel = moduleNames[moduleId] || "Module";

  const currentModuleMenuItem = useMemo(() => {
    const normalizedLabel = String(currentModuleLabel).trim().toLowerCase();

    return (
      getMenuList.find(
        (item) => String(item?.label).trim().toLowerCase() === normalizedLabel
      ) || null
    );
  }, [currentModuleLabel, getMenuList]);

  const handleViewClick = (notification) => {
    navigate(routes.information, {
      state: {
        moduleId: Number(moduleId),
        notificationId: notification?.id ?? notification?._id,
        notification,
        id: currentModuleMenuItem?.id ?? Number(moduleId),
        label: currentModuleMenuItem?.label || currentModuleLabel,
        submenuItems: currentModuleMenuItem?.submenuItems || [],
      },
    });
  };

  const liveModuleNotifications = useMemo(() => {
    const currentModuleKey = Number(moduleId);

    return notificationlist.filter((notification) => {
      const moduleKey = normalizeModuleKey(notification);
      return moduleKey !== null && Number(moduleKey) === currentModuleKey;
    });
  }, [moduleId, notificationlist]);

  // Fetch notification data
  const fetchNotificationData = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.createdBy) {
        params.append("created_by", filters.createdBy.value.toString());
      }
      if (filters.date) {
        params.append("date", filters.date);
      }
      params.append("module_id", moduleId);

      const response = await fetch(
        `https://api.mirai.healthcare/notification?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "x-auth": `${authtoken}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.message === "notifications" && Array.isArray(data.data)) {
        const sortedNotifications = mergeNotifications(data.data, liveModuleNotifications);
        setDisplayNotifications(sortedNotifications);
        setFilteredNotifications(sortedNotifications);
      } else {
        console.error("Invalid API response format:", data);
        setDisplayNotifications([]);
        setFilteredNotifications([]);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch notifications.",
        confirmButtonColor: "#ec4899",
      });
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      createdBy: null,
      date: "",
    });
    fetchNotificationData();
    setPage(1);
  };

  // Mark notification as read
  const markNotificationAsRead = (notificationId) => {
    if (socket && socket.connected) {
      socket.emit("markNotificationAsRead", {
        notificationId,
        schema: userData?.currentUser?.schema,
      });
      setDisplayNotifications((prevNotifications) =>
        prevNotifications.map((notif) =>
          notif.id === notificationId || notif._id === notificationId
            ? { ...notif, seen: true }
            : notif
        )
      );
      setFilteredNotifications((prevNotifications) =>
        prevNotifications.map((notif) =>
          notif.id === notificationId || notif._id === notificationId
            ? { ...notif, seen: true }
            : notif
        )
      );
      if (typeof setNotificationList === "function") {
        setNotificationList((prevList) =>
          prevList.map((notif) =>
            notif.id === notificationId || notif._id === notificationId
              ? { ...notif, seen: true }
              : notif
          )
        );
      }
    } else {
      console.warn("Socket not connected. Cannot mark notification as read.");
      Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "Socket not connected. Cannot mark notification as read.",
        confirmButtonColor: "#ec4899",
      });
    }
  };

  // Mark notification as read and responded
  const markNotificationAsReadandResponded = (notificationId, action) => {
    if (socket && socket.connected) {
      socket.emit("markNotificationAsReadandResponse", {
        notificationId,
        schema: userData?.currentUser?.schema,
      });
      setDisplayNotifications((prevNotifications) =>
        prevNotifications.map((notif) =>
          notif.id === notificationId || notif._id === notificationId
            ? { ...notif, response_status: action, seen: true }
            : notif
        )
      );
      setFilteredNotifications((prevNotifications) =>
        prevNotifications.map((notif) =>
          notif.id === notificationId || notif._id === notificationId
            ? { ...notif, response_status: action, seen: true }
            : notif
        )
      );
      if (typeof setNotificationList === "function") {
        setNotificationList((prevList) =>
          prevList.map((notif) =>
            notif.id === notificationId || notif._id === notificationId
              ? { ...notif, response_status: action, seen: true }
              : notif
          )
        );
      }
    } else {
      console.warn("Socket not connected. Cannot mark notification as read.");
      Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "Socket not connected. Cannot mark notification as read.",
        confirmButtonColor: "#ec4899",
      });
    }
  };

  // Handle response actions (Accept/Wait)
  const handleResponseAction = async (notificationId, action, responseUrl) => {
    if (!responseUrl) {
      console.error("No response URL provided for this notification.");
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No response URL provided for this notification.",
        confirmButtonColor: "#ec4899",
      });
      return;
    }

    try {
      const response = await fetch(responseUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-auth": `${authtoken}`,
        },
        body: JSON.stringify({
          status: action,
        }),
      });

      if (response.ok) {
        markNotificationAsReadandResponded(notificationId, action);
        Swal.fire({
          icon: "success",
          title: "Success",
          text: `Notification ${action} successfully.`,
          confirmButtonColor: "#ec4899",
        });
      } else {
        const errorData = await response.json();
        console.error(`Failed to ${action} notification ${notificationId}:`, errorData.message);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: `Failed to ${action} notification: ${errorData.message}`,
          confirmButtonColor: "#ec4899",
        });
      }
    } catch (error) {
      console.error(`Error during ${action} action for notification ${notificationId}:`, error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Error during ${action} action: ${error.message}`,
        confirmButtonColor: "#ec4899",
      });
    }
  };

  // Render payload content
  const renderPayload = (url) => {
    if (!url) return null;

    const isImage = /\.(jpeg|jpg|gif|png|webp|avif)$/i.test(url);
    const isPdf = /\.pdf$/i.test(url);

    if (isImage) {
      return (
        <div className="notification-payload-image mt-3">
          <img src={url} alt="Notification Payload" className="img-fluid rounded" style={{ maxWidth: "100%", height: "auto" }} />
        </div>
      );
    } else if (isPdf) {
      return (
        <div className="notification-payload-pdf mt-3">
          <Button
            variant="info"
            size="sm"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
          >
            View PDF Document
          </Button>
        </div>
      );
    } else {
      return (
        <div className="notification-payload-generic mt-3">
          <p>File content:</p>
          <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary">
            Open File
          </a>
        </div>
      );
    }
  };

  // Calculate "time ago" format
  const timeAgo = (dateString) => {
    const now = new Date();
    const past = new Date(dateString);
    const diffInMs = now - past;
    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInSeconds < 60) {
      return `${diffInSeconds} seconds ago`;
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes === 1 ? "" : "s"} ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours === 1 ? "" : "s"} ago`;
    } else if (diffInDays < 30) {
      return `${diffInDays} day${diffInDays === 1 ? "" : "s"} ago`;
    } else {
      return past.toLocaleDateString();
    }
  };

  useEffect(() => {
    fetchNotificationData();
  }, [filters, moduleId]);

  const visibleNotifications = useMemo(
    () => mergeNotifications(filteredNotifications, liveModuleNotifications),
    [filteredNotifications, liveModuleNotifications]
  );

  // Paginate the filtered data
  const paginatedData = visibleNotifications.slice(0, page * pageSize);

  return (
    <div className="page-wrapper">
      <div className="content">
        <Header />
        <Container fluid className="py-4">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">
                <i className="ti ti-bell me-2"></i>Notifications - {currentModuleLabel}
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <a href="#" onClick={() => navigate(-1)}>Back to Modules</a>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Notifications
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <Button
                variant="outline-secondary"
                className="mb-2"
                onClick={() => navigate(-1)}
              >
                <i className="ti ti-arrow-left me-2"></i>Back to Modules
              </Button>
            </div>
          </div>
          {/* Filter Inputs */}
          {/* <div className="row mb-3">
            <div className="col-md-4">
              <label className="form-label">Created By</label>
              <Select
                options={staffOptions}
                value={filters.createdBy}
                onChange={(value) => handleFilterChange("createdBy", value)}
                placeholder="Select Created By"
                isClearable
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Date</label>
              <Form.Control
                type="date"
                value={filters.date}
                onChange={(e) => handleFilterChange("date", e.target.value)}
                placeholder="Select Date"
              />
            </div>
            <div className="col-md-4 d-flex align-items-end">
              <Button
                className="btn btn-primary me-2"
                onClick={() => {
                  fetchNotificationData();
                  setPage(1);
                }}
              >
                Search
              </Button>
              <Button
                className="btn btn-light"
                onClick={resetFilters}
              >
                Reset
              </Button>
            </div>
          </div> */}
          {/* Notifications Timeline */}
          <Card className="shadow-sm border-0">
            <Card.Header className="d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Notification Timeline</h4>
            </Card.Header>
            <Card.Body className="py-3">
              {visibleNotifications.length > 0 ? (
                <>
                  <div className="timeline position-relative">
                    {paginatedData.map((notification, index) => (
                      <div key={notification.id || index} className="timeline-item mb-4">
                        <div className="timeline-dot bg-primary rounded-circle">
                          <i
                            className="ti ti-bell text-white"
                            style={{ fontSize: "12px", position: "relative", top: "2px" }}
                          />
                        </div>
                        <div className="timeline-content card shadow-sm p-4 position-relative">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5 className="mb-0">
                              <i className="ti ti-megaphone me-2 text-primary" />
                              New Notification
                              {!notification.seen && (
                                <span className="badge bg-primary ms-2">New</span>
                              )}
                            </h5>
                            <span className="badge bg-light text-dark">
                              {currentModuleLabel}
                            </span>
                          </div>
                          <p>
                            <i className="ti ti-info-circle me-2 text-muted" />
                            {getNotificationText(notification)}
                          </p>
                          <div className="row">
                            <div className="col-md-6">
                             
                              <p className="mb-1">
                                <i className="ti ti-clock me-2 text-primary" />
                                <strong>Posted At:</strong> {timeAgo(notification.created_at)}
                              </p>
                            </div>
                            <div className="col-md-6">
                              {notification.type === "payload" && renderPayload(notification.payload_data)}
                              {notification.type === "response" && getNotificationStatus(notification).toLowerCase() !== "pending" && (
                                <p className="mb-1">
                                  <i className="ti ti-info-circle me-2 text-primary" />
                                  <strong>Status:</strong>{" "}
                                  <span
                                    className={`badge ${
                                      getNotificationStatus(notification)?.toLowerCase() === "accepted"
                                        ? "bg-success"
                                        : getNotificationStatus(notification)?.toLowerCase() === "waiting"
                                        ? "bg-warning"
                                        : "bg-light"
                                    }`}
                                  >
                                    {getNotificationStatus(notification)}
                                  </span>
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="d-flex justify-content-end gap-2 mt-3 flex-wrap">
                            <Button
                              variant="outline-primary"
                              className="btn-sm d-flex align-items-center"
                              onClick={() => handleViewClick(notification)}
                            >
                              <i className="ti ti-eye me-2" />
                              View
                            </Button>
                          </div>
                          {notification.type === "response" && getNotificationStatus(notification).toLowerCase() === "pending" && (
                            <div className="d-flex justify-content-end gap-2 mt-3">
                              <Button
                                variant="success"
                                className="btn-sm d-flex align-items-center"
                                onClick={() =>
                                  handleResponseAction(notification.id, "ACCEPTED", notification.response_url)
                                }
                              >
                                <i className="ti ti-check me-2" />
                                Accept
                              </Button>
                              <Button
                                variant="warning"
                                className="btn-sm d-flex align-items-center"
                                onClick={() =>
                                  handleResponseAction(notification.id, "WAITING", notification.response_url)
                                }
                              >
                                <i className="ti ti-clock me-2" />
                                Wait
                              </Button>
                            </div>
                          )}
                          {!notification.seen && (
                            <div className="d-flex justify-content-end gap-2 mt-3">
                              <Button
                                variant="outline-primary"
                                className="btn-sm d-flex align-items-center"
                                onClick={() => markNotificationAsRead(notification.id)}
                              >
                                <i className="ti ti-check me-2" />
                                Mark as Read
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {visibleNotifications.length > paginatedData.length && (
                    <div className="text-center mt-4">
                      <Button
                        className="btn btn-primary d-flex align-items-center mx-auto"
                        onClick={() => setPage(page + 1)}
                      >
                        <i className="ti ti-chevron-down me-2" />
                        Load More
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-5">
                  <h5>
                    <i className="ti ti-alert-triangle me-2 text-warning" />
                    No Notifications Found
                  </h5>
                  <p>Try adjusting your filters to find notifications.</p>
                </div>
              )}
            </Card.Body>
          </Card>
          {/* Inline CSS for Timeline Styling */}
          <style>{`
            .timeline {
              padding-left: 30px;
            }
            .timeline::before {
              content: '';
              position: absolute;
              left: 15px;
              top: 0;
              bottom: 0;
              width: 2px;
              background-color: #dee2e6;
            }
            .timeline-item {
              position: relative;
            }
            .timeline-dot {
              position: absolute;
              left: -30px;
              top: 10px;
              width: 20px;
              height: 20px;
              border: 3px solid #fff;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .timeline-content {
              border-radius: 8px;
            }
          `}</style>
        </Container>
      </div>
    </div>
  );
};

export default ModuleWiseNotification;
